import React, { useState, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useFinancialYear } from '../context/FinancialYearContext';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { DollarSign, PieChart, Calendar, ArrowRightLeft, RefreshCw, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import FunderCorporatePnL from '../components/pnl/FunderCorporatePnL';
import PnLReconciliation from '../components/pnl/PnLReconciliation';
import QuarterlyStatement from '../components/pnl/QuarterlyStatement';
import EstimateInvoicePaid from '../components/pnl/EstimateInvoicePaid';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PnLReportPage = () => {
  const { getAuthHeaders, user } = useAuth();
  const { selectedFY: globalFY } = useFinancialYear();
  const [activeTab, setActiveTab] = useState('statement');
  const [refreshKey, setRefreshKey] = useState(0);
  const [syncing, setSyncing] = useState(false);

  const isAdmin = user?.role === 'ADMIN';

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount || 0);

  const pollJob = async (jobId) => {
    for (let i = 0; i < 60; i++) {
      await new Promise((r) => setTimeout(r, 3000));
      try {
        const res = await axios.get(`${API}/zoho/sync-jobs/${jobId}`, { headers: getAuthHeaders() });
        const st = res.data.status;
        if (st === 'success') return res.data;
        if (st === 'failed' || st === 'error') throw new Error(res.data.error || 'Sync failed');
      } catch (e) {
        if (i > 3) throw e;
      }
    }
    throw new Error('Sync timed out');
  };

  const handleSyncAll = useCallback(async () => {
    if (!isAdmin) { toast.error('Only Admin can sync'); return; }
    try {
      setSyncing(true);
      toast.info('Refetching estimates, CRM and invoices…');
      const res = await axios.post(`${API}/pnl/sync-all`, {}, { headers: getAuthHeaders() });
      await pollJob(res.data.job_id);
      toast.success('Sync complete — reloading figures');
      setRefreshKey((k) => k + 1);
    } catch (e) {
      toast.error(`Sync failed: ${e.message || 'unknown error'}`);
    } finally {
      setSyncing(false);
    }
  }, [isAdmin, getAuthHeaders]);

  const childProps = { globalFY, getAuthHeaders, formatCurrency };
  const k = `${globalFY}-${refreshKey}`;

  return (
    <div className="space-y-6" data-testid="pnl-report-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-xl bg-slate-900 text-white"><PieChart className="w-6 h-6" /></div>
          <div>
            <h1 className="text-3xl font-bold text-slate-900">New P&amp;L Report</h1>
            <p className="text-sm text-slate-500">2026 onwards · estimate-driven revenue, funder share &amp; reconciliation — FY {globalFY}</p>
          </div>
        </div>
        {isAdmin && (
          <Button onClick={handleSyncAll} disabled={syncing} data-testid="pnl-report-sync-all" className="bg-slate-900 hover:bg-slate-800">
            {syncing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Syncing…</> : <><RefreshCw className="w-4 h-4 mr-2" />Sync Zoho Data</>}
          </Button>
        )}
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-100 p-1 flex-wrap h-auto">
          <TabsTrigger value="statement" className="data-[state=active]:bg-white" data-testid="tab-fc-statement">
            <DollarSign className="w-4 h-4 mr-2" />Funder &amp; Corporate P&amp;L
          </TabsTrigger>
          <TabsTrigger value="reconciliation" className="data-[state=active]:bg-white" data-testid="tab-reconciliation">
            <PieChart className="w-4 h-4 mr-2" />Reconciliation
          </TabsTrigger>
          <TabsTrigger value="quarterly" className="data-[state=active]:bg-white" data-testid="tab-quarterly">
            <Calendar className="w-4 h-4 mr-2" />Quarterly
          </TabsTrigger>
          <TabsTrigger value="estimate-invoice-paid" className="data-[state=active]:bg-white" data-testid="tab-estimate-invoice-paid">
            <ArrowRightLeft className="w-4 h-4 mr-2" />Estimate → Invoice → Paid
          </TabsTrigger>
        </TabsList>

        <TabsContent value="statement" className="mt-6" data-testid="pane-fc-statement">
          <FunderCorporatePnL key={`s-${k}`} {...childProps} />
        </TabsContent>
        <TabsContent value="reconciliation" className="mt-6" data-testid="pane-reconciliation">
          <PnLReconciliation key={`r-${k}`} {...childProps} />
        </TabsContent>
        <TabsContent value="quarterly" className="mt-6" data-testid="pane-quarterly">
          <QuarterlyStatement key={`q-${k}`} {...childProps} />
        </TabsContent>
        <TabsContent value="estimate-invoice-paid" className="mt-6" data-testid="pane-estimate-invoice-paid">
          <EstimateInvoicePaid key={`e-${k}`} {...childProps} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default PnLReportPage;
